import { StandardEvents } from '@shopify/events';

/**
 * Golden Hour: the second Add to cart (sections/ghw-buy-again.liquid).
 *
 * 1. Keep the line "Black, size 6.5" and the button in step with the picker
 *    in the buy column. The picker's form carries the chosen variant in its
 *    own `id` input; that is read after every product-select event, the
 *    same way ghw-bundle.js does it.
 * 2. On submit, press Horizon's own Add to cart in that form, as Horizon's
 *    sticky bar does, so the cart drawer and cart events stay Horizon's.
 *    If that button cannot be found, the form posts to /cart/add as is.
 * 3. "Change colour or size" scrolls to the picker instead of the page top.
 * 4. With a pack chosen in ghw-bundle, show the pack instead: every pair in
 *    words, the pack price, and the button off until every pair is chosen.
 *    The pack announces itself with a `ghw:bundle` event.
 */
class GhwBuyAgain extends HTMLElement {
  #variants = [];
  #names = [];
  #abort = new AbortController();
  /** @type {{count:number, lines:string[], price:string, ready:boolean} | null} */
  #bundle = null;

  connectedCallback() {
    try {
      this.#variants = JSON.parse(this.querySelector('[data-ghw-again-variants]')?.textContent || '[]');
      this.#names = JSON.parse(this.querySelector('[data-ghw-again-names]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }

    const { signal } = this.#abort;
    this.querySelector('form')?.addEventListener('submit', this.#onSubmit, { signal });
    this.querySelector('[data-ghw-again-change]')?.addEventListener('click', this.#onChange, { signal });
    document.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    document.addEventListener('ghw:bundle', this.#onBundle, { signal });
    document.addEventListener('ghw:size-pending', () => this.#sync(), { signal });
    this.#sync();
  }

  #onBundle = (event) => {
    this.#bundle = event.detail ?? null;
    this.#sync();
  };

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** The product form in the buy column, not a quick-add dialog. */
  get #form() {
    return Array.from(document.querySelectorAll('product-form-component form[action*="/cart/add"]')).find(
      (f) => !f.closest('quick-add-dialog, .quick-add-modal')
    );
  }

  #onProductSelect = async (event) => {
    try {
      await event.promise;
    } catch {
      /* aborted; the next event lands */
    }
    this.#sync();
  };

  #sync() {
    const input = this.#form?.querySelector('input[name="id"]');
    const variant = this.#variants.find((v) => String(v.id) === String(input?.value));
    const choice = this.querySelector('[data-ghw-again-choice]');
    const hidden = this.querySelector('[data-ghw-again-id]');
    const button = this.querySelector('[data-ghw-again-button]');
    const price = this.querySelector('.ghw-again__price');
    if (!choice || !hidden || !button) return;

    const pack = this.#bundle;
    if (pack && pack.count > 1) {
      const done = pack.lines.filter(Boolean);
      const word = this.dataset.packWord || 'pairs';
      choice.textContent =
        done.length === pack.count
          ? `${pack.count} ${word}: ${done.join(' and ')}`
          : `${pack.count} ${word}: ${this.dataset.packMissing || 'choose a colour and size for each above'}`;
      if (price) price.textContent = pack.price;
      hidden.value = variant ? String(variant.id) : '';
      // Held, not disabled: a tap takes her to the open row (ghw-bundle's
      // guard). Only a real sold-out variant switches the button off.
      button.disabled = !!variant && !variant.available;
      if (pack.ready) button.removeAttribute('aria-disabled');
      else button.setAttribute('aria-disabled', 'true');
      button.textContent = button.dataset.add;
      return;
    }

    // No size chosen above yet (ghw-size-pending.js): say so, hold the
    // button, and post nothing even without the script that intercepts.
    if (document.documentElement.hasAttribute('data-ghw-size-pending') || !variant) {
      choice.textContent = this.dataset.sizeMissing || 'Choose your size above';
      hidden.value = '';
      // Held, not disabled: a tap takes her to the sizes
      // (ghw-size-pending's guard), and the empty id posts nothing.
      button.disabled = false;
      button.setAttribute('aria-disabled', 'true');
      button.textContent = button.dataset.add;
      return;
    }

    choice.textContent = variant.options
      .map((value, i) => (i === 0 ? value : `${(this.#names[i] || '').toLowerCase()} ${value}`.trim()))
      .join(', ');
    hidden.value = variant.id;
    if (price) price.textContent = variant.price;
    button.disabled = !variant.available;
    button.removeAttribute('aria-disabled');
    button.textContent = variant.available ? button.dataset.add : button.dataset.soldOut;
  }

  #onSubmit = (event) => {
    this.#sync();
    const button = this.querySelector('[data-ghw-again-button]');
    const target = this.#form?.querySelector('[ref="addToCartButton"], button[type="submit"]');
    // With the product form on the page this form never posts itself: the
    // buy column decides what goes in the cart (one pair or a whole pack).
    // If that button is off, something above is not chosen yet; go there.
    // Only without the product form does the plain post to /cart/add run.
    if (!target) return;
    event.preventDefault();
    if (button.getAttribute('aria-disabled') === 'true' || target.disabled || target.getAttribute('aria-disabled') === 'true') {
      this.#onChange(event);
      return;
    }
    target.click();
  };

  #onChange = (event) => {
    const packChosen = this.#bundle && this.#bundle.count > 1;
    const picker = document.querySelector(packChosen ? 'ghw-bundle-component' : '.variant-picker');
    if (!picker) return;
    event.preventDefault();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    picker.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
  };
}

if (!customElements.get('ghw-buy-again')) {
  customElements.define('ghw-buy-again', GhwBuyAgain);
}
